import { roundQuantity } from '../schedule/unit-of-measure';
import type { LaborRateSource } from './labor-rate';
import { materialTotal, TOTAL_DECIMALS, type MaterialPriceOrigin } from './material-price';
import { serviceKey } from './service-catalog';
import { isPurchasable, type ItemKind } from './takeoff-line';

/**
 * O orçamento da obra (Fase 29, etapa 4 — D15, D16, RN-401 a RN-403).
 *
 * A aba Orçamento mostra a obra em três níveis — disciplina → serviço → item — com o total de
 * cada um, e a soma é o **custo direto**: material mais mão de obra, sem BDI (D17). A mesma
 * linha serve ao orçamento vivo, que a tela monta da consolidação, e ao **aprovado**, que é a
 * foto inteira guardada no servidor: quantidade, preço unitário e total de cada linha, com data
 * e autor. A foto não muda se o desenho, o compras ou a folha mudarem depois.
 *
 * O total de cada linha é arredondado nela, e o da obra é a soma das linhas — como num
 * orçamento de papel. A Consolidação arredonda por material inteiro, e por isso pode diferir em
 * centavos quando um material é usado por vários serviços.
 */

/** De onde veio o preço da linha: os do material (RN-398) e os da função (RN-399). */
export type BudgetPriceOrigin = MaterialPriceOrigin | LaborRateSource;

export const BUDGET_PRICE_ORIGINS: readonly BudgetPriceOrigin[] = [
  'MEDIO',
  'PROVISORIO',
  'DIGITADO',
  'OBRA',
  'SISTEMA',
];

/** O teto de linhas de um orçamento — uma obra grande tem poucos milhares. */
export const MAX_BUDGET_LINES = 20_000;

/** Uma linha: o que um serviço consome de um item, com o preço que valia. */
export interface BudgetLineInput {
  readonly disciplineName: string;
  readonly serviceKey: string;
  readonly serviceName: string;
  readonly kind: ItemKind;
  /** A chave da consolidação: `codigo:`, `descricao:` ou `funcao:`. */
  readonly itemKey: string;
  readonly code?: string;
  readonly description: string;
  readonly unit?: string;
  readonly quantity: number;
  /** Por unidade do item; na mão de obra, o R$/h. Ausente = sem preço, e fora do total. */
  readonly unitPrice?: number;
  readonly priceOrigin?: BudgetPriceOrigin;
  /** O aviso que a tela mostrava na linha ("unidade diferente — falta o fator"…), em texto. */
  readonly warning?: string;
}

export interface BudgetLine extends BudgetLineInput {
  /** Quantidade × preço, em centavos. Ausente = fora do total. */
  readonly total?: number;
}

/** O total da linha. Quem guarda a foto o refaz aqui, e não confia no total de quem mandou. */
export function priceBudgetLine(line: BudgetLineInput): BudgetLine {
  return line.unitPrice === undefined
    ? line
    : { ...line, total: materialTotal(line.quantity, line.unitPrice) };
}

export interface BudgetTotals {
  readonly material: number;
  readonly labor: number;
  /** Material + mão de obra: o custo direto. */
  readonly total: number;
  /** Linhas sem preço — o total está incompleto enquanto houver. */
  readonly linesOutOfTotal: number;
}

export interface BudgetSummary extends BudgetTotals {
  /** Materiais (pela chave) sem preço, como a Consolidação os conta. */
  readonly materialsWithoutPrice: number;
  /** Itens de mão de obra (pela chave) sem R$/h. */
  readonly laborWithoutRate: number;
}

function somar(lines: readonly BudgetLine[]): BudgetTotals {
  let material = 0;
  let labor = 0;
  let linesOutOfTotal = 0;
  for (const line of lines) {
    if (line.total === undefined) {
      linesOutOfTotal += 1;
      continue;
    }
    if (isPurchasable(line.kind)) material += line.total;
    else labor += line.total;
  }
  const m = roundQuantity(material, TOTAL_DECIMALS);
  const l = roundQuantity(labor, TOTAL_DECIMALS);
  return { material: m, labor: l, total: roundQuantity(m + l, TOTAL_DECIMALS), linesOutOfTotal };
}

export function summarizeBudget(lines: readonly BudgetLine[]): BudgetSummary {
  const semPreco = new Set<string>();
  const semCusto = new Set<string>();
  for (const line of lines) {
    if (line.total !== undefined) continue;
    (isPurchasable(line.kind) ? semPreco : semCusto).add(line.itemKey);
  }
  return {
    ...somar(lines),
    materialsWithoutPrice: semPreco.size,
    laborWithoutRate: semCusto.size,
  };
}

export interface BudgetService extends BudgetTotals {
  readonly key: string;
  readonly name: string;
  readonly lines: readonly BudgetLine[];
}

export interface BudgetDiscipline extends BudgetTotals {
  readonly name: string;
  readonly services: readonly BudgetService[];
}

/**
 * Disciplina → serviço → linhas, cada nível com o seu total, na ordem em que aparecem.
 *
 * A disciplina é agrupada pelo nome — é o que a consolidação carrega em cada uso — e o serviço
 * pela chave dentro dela.
 */
export function groupBudget(lines: readonly BudgetLine[]): readonly BudgetDiscipline[] {
  const disciplinas = new Map<string, Map<string, { name: string; lines: BudgetLine[] }>>();
  for (const line of lines) {
    let servicos = disciplinas.get(line.disciplineName);
    if (servicos === undefined) {
      servicos = new Map();
      disciplinas.set(line.disciplineName, servicos);
    }
    let servico = servicos.get(line.serviceKey);
    if (servico === undefined) {
      servico = { name: line.serviceName, lines: [] };
      servicos.set(line.serviceKey, servico);
    }
    servico.lines.push(line);
  }
  return [...disciplinas].map(([name, servicos]) => {
    const services = [...servicos].map(([key, s]): BudgetService => ({
      key,
      name: s.name,
      lines: s.lines,
      ...somar(s.lines),
    }));
    return { name, services, ...somar(services.flatMap((s) => s.lines)) };
  });
}

// ── A trava por serviço (Fase 33) ───────────────────────────────────────────────

/**
 * O sufixo que a cópia da obra põe no nome do serviço da obra numa disciplina global (`0061`):
 * "ESTACA" vira "ESTACA (V2)" na versão, e a chave do catálogo (`disciplina|serviço`, em
 * minúsculas) ganha o " (v2)" no fim.
 */
const SUFIXO_DA_VERSAO = / \(v\d+\)$/u;

/**
 * A chave da trava do serviço (Fase 33, RN-435): a do catálogo, **sem** o sufixo da versão.
 *
 * O mesmo serviço tem uma chave na aprovada e outra na versão que a copiou. A trava tem de ser
 * a mesma nas duas — senão duplicar a obra destravaria o serviço, e aprovar a versão o
 * reabriria (D3, D7). A `0076` tira o mesmo sufixo das fotos antigas, com a mesma expressão.
 */
export function serviceLockKey(serviceKey: string): string {
  return serviceKey.replace(SUFIXO_DA_VERSAO, '');
}

/** A chave da trava do serviço do catálogo `(disciplina, serviço)` — a que a gravação confere. */
export function serviceLockKeyOf(disciplineName: string, serviceName: string): string {
  return serviceLockKey(serviceKey(disciplineName, serviceName));
}

/** Um serviço aprovado: a trava, com o nome que ele tinha na foto (RN-435). */
export interface ApprovedService {
  /** A chave da trava (`serviceLockKey`). */
  readonly key: string;
  readonly disciplineName: string;
  readonly serviceName: string;
}

/** "FUNDAÇÃO › ESTACA" — como as recusas e a tela nomeiam o serviço. */
export function approvedServiceLabel(service: Omit<ApprovedService, 'key'>): string {
  return `${service.disciplineName} › ${service.serviceName}`;
}

/** "A", "A e B", "A, B e C". */
function emLista(nomes: readonly string[]): string {
  if (nomes.length <= 1) return nomes.join('');
  return `${nomes.slice(0, -1).join(', ')} e ${nomes.at(-1)!}`;
}

/** "A", "A e B", "A, B e C" — a recusa diz quais serviços são. */
export function approvedServiceLabels(services: readonly Omit<ApprovedService, 'key'>[]): string {
  return emLista(services.map(approvedServiceLabel));
}

/**
 * A recusa da quantidade travada (Fase 33, RN-438), pelos nomes (`approvedServiceLabel`).
 *
 * Num texto só para o servidor e a tela: o servidor a devolve na recusa, e a tela a repete no
 * aviso da gravação que pulou o serviço aprovado e gravou o resto.
 */
export function lockedServicesMessage(labels: readonly string[]): string {
  return labels.length === 1
    ? `O serviço ${labels[0]!} foi aprovado no orçamento, e a quantidade dele não muda mais.`
    : `Os serviços ${emLista(labels)} foram aprovados no orçamento, e a quantidade deles não muda mais.`;
}

/** Os serviços das linhas, um por chave da trava, na ordem em que aparecem. */
export function servicesOfLines(lines: readonly BudgetLineInput[]): readonly ApprovedService[] {
  const vistos = new Map<string, ApprovedService>();
  for (const line of lines) {
    const key = serviceLockKey(line.serviceKey);
    if (vistos.has(key)) continue;
    vistos.set(key, {
      key,
      disciplineName: line.disciplineName,
      serviceName: line.serviceName,
    });
  }
  return [...vistos.values()];
}

/** O orçamento aprovado: a foto dos serviços aprovados juntos, com data e autor (RN-402). */
export interface ApprovedBudget {
  readonly projectId: string;
  /** ISO 8601. */
  readonly approvedAt: string;
  readonly approvedByUserId?: string;
  readonly approvedByName?: string;
  /** O período da foto do compras que valia (RN-397). */
  readonly pricePeriod?: { readonly from: string; readonly to: string };
  /** O mês das horas do R$/h, `AAAA-MM` (RN-399). */
  readonly laborMonth?: string;
  readonly summary: BudgetSummary;
  /** Os serviços que esta foto trava (Fase 33, RN-435) — os das linhas dela. */
  readonly services: readonly ApprovedService[];
  readonly lines: readonly BudgetLine[];
}

/** Quando e por quem o serviço foi aprovado — o que a tela diz ao lado do cadeado. */
export interface ServiceApproval {
  readonly approvedAt: string;
  readonly approvedByName?: string;
}

/** O orçamento da obra com os serviços aprovados lidos da foto (RN-437). */
export interface ProjectBudgetView {
  readonly lines: readonly BudgetLine[];
  /** Os serviços aprovados, pela chave da trava. */
  readonly approved: ReadonlyMap<string, ServiceApproval>;
}

/**
 * O orçamento da obra (Fase 33, RN-437): o serviço aprovado é a foto dele; o aberto, o de hoje.
 *
 * As linhas de hoje chegam na ordem da obra (a do catálogo). As do serviço aprovado entram no
 * lugar onde ele aparece hoje, inteiras e como a foto as guardou. O serviço aprovado que hoje
 * não tem linha — saiu do catálogo, ou o desenho o zerou — vai para o fim, e não some:
 * orçamento com serviço aprovado escondido mente.
 */
export function mergeApprovedBudgets(
  live: readonly BudgetLine[],
  approvals: readonly ApprovedBudget[],
): ProjectBudgetView {
  const daFoto = new Map<string, BudgetLine[]>();
  const approved = new Map<string, ServiceApproval>();
  for (const approval of approvals) {
    const quando: ServiceApproval = {
      approvedAt: approval.approvedAt,
      ...(approval.approvedByName === undefined ? {} : { approvedByName: approval.approvedByName }),
    };
    for (const service of approval.services) approved.set(service.key, quando);
    for (const line of approval.lines) {
      const key = serviceLockKey(line.serviceKey);
      const doServico = daFoto.get(key);
      if (doServico === undefined) daFoto.set(key, [line]);
      else doServico.push(line);
    }
  }

  const lines: BudgetLine[] = [];
  const postos = new Set<string>();
  for (const line of live) {
    const key = serviceLockKey(line.serviceKey);
    if (!approved.has(key)) {
      lines.push(line);
      continue;
    }
    if (postos.has(key)) continue;
    postos.add(key);
    lines.push(...(daFoto.get(key) ?? []));
  }
  for (const [key, doServico] of daFoto) {
    if (!postos.has(key)) lines.push(...doServico);
  }
  return { lines, approved };
}
